import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useSearchParams, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Компоненты
import { Card } from 'react-bootstrap';
import TransactionsTable from './TransactionsTable';
import TransactionsFilters from './TransactionsFilters';

// Хелперы
import getQueryParams from 'helpers/getQueryParams.hl';

import ApiService from '../../helpers/apiService';

// Типы
import { ITransaction, ITransactionResponse } from './types';

type FiltersTypes = {
  limit: number;
  page: number;
  from?: string;
  to?: string;
  trxId?: string;
  accountId?: string;
  currencies?: string[];
};

const Transactions = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<{
    countAll: number;
    data: ITransaction[];
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { transactionId } = useParams(); // Получаем параметр transactionId из URL

  const params = getQueryParams(searchParams);

  const handlerSubmit = async () => {
    setLoading(true);
    const rawData: Record<string, any> = {
      ...values,
      size: values.limit,
      ...(transactionId ? { trxId: transactionId } : {}),
      ...(values?.currencies?.length
        ? {
            currencies: values?.currencies?.map(
              (currency: { value: string; label: string }) => currency?.value
            )
          }
        : {})
    };
    // Убираем пустые строки — бэкенд отклоняет пустой accountId/trxId/from/to
    const preparedData = Object.fromEntries(
      Object.entries(rawData).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
    setSearchParams(preparedData as any);
    try {
      const data = await ApiService.fetchTransactions<ITransactionResponse>(
        preparedData
      );
      setTransactions(data);
    } catch (err: any) {
      console.error('Ошибка при загрузке транзакций:', err);
      const isNetworkError = err?.message === 'Network Error' || !err?.response;
      const msg = isNetworkError
        ? 'Сервер недоступен. Проверьте, что API запущен (например, localhost:8085) и нет блокировки CORS.'
        : (err?.response?.data?.message ?? err?.message) || 'Не удалось загрузить транзакции';
      toast.error(typeof msg === 'string' ? msg : 'Ошибка сервера. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page: number) => {
    if (!page) return;
    setLoading(true);
    const rawPage: Record<string, any> = {
      ...(params as FiltersTypes),
      page,
      size: values.limit,
      ...(params?.trxId ? { trxId: params.trxId } : {})
    };
    const preparedData = Object.fromEntries(
      Object.entries(rawPage).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
    setSearchParams(preparedData as any);
    await setFieldValue('page', page);
    try {
      const data = await ApiService.fetchTransactions<ITransactionResponse>(
        preparedData
      );
      setTransactions(data);
    } catch (err: any) {
      console.error('Ошибка при загрузке транзакций:', err);
      const isNetworkError = err?.message === 'Network Error' || !err?.response;
      const msg = isNetworkError
        ? 'Сервер недоступен. Проверьте, что API запущен (например, localhost:8085) и нет блокировки CORS.'
        : (err?.response?.data?.message ?? err?.message) || 'Не удалось загрузить транзакции';
      toast.error(typeof msg === 'string' ? msg : 'Ошибка сервера. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  const {
    handleSubmit,
    setFieldValue,
    setValues,
    values,
    values: { page, limit }
  } = useFormik({
    initialValues: {
      page: params?.page ? Number(params.page) : 1,
      limit: params?.limit ? Number(params.limit) : 20,
      from: params?.from || '',
      to: params?.to || '',
      accountId: params?.accountId || '',
      trxId: transactionId || params?.trxId || '',
      currencies: [] as any
    },
    onSubmit: handlerSubmit
  });

  useEffect(() => {
    handlerSubmit();
    if (!Object.entries(params)?.length) return;
    const preparedParams = {
      ...params,
      ...(params?.trxId ? { trxId: params.trxId } : {}),
      ...(params?.currencies
        ? {
            currencies: Array.isArray(params?.currencies)
              ? params?.currencies?.map((currency: string) => ({
                  value: currency,
                  label: currency
                }))
              : [
                  {
                    value: params?.currencies,
                    label: params?.currencies
                  }
                ]
          }
        : {})
    };
    setValues(preparedParams as any);
  }, [transactionId]); // Добавляем зависимость от transactionId

  const availableCurrencies = React.useMemo(() => {
    const list = transactions?.data ?? [];
    const set = new Set<string>();
    list.forEach(t => {
      if (t?.currency) set.add(t.currency);
    });
    return Array.from(set).sort().map(c => ({ value: c, label: c }));
  }, [transactions?.data]);

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h3 className="mb-0">Транзакции</h3>
          <p className="mt-1 mb-0 text-muted small">Поиск и фильтрация операций</p>
        </Card.Body>
      </Card>
      <Card>
        <Card.Header>
          <TransactionsFilters
            values={values}
            handleSubmit={handleSubmit}
            setFieldValue={setFieldValue}
            loading={loading}
            availableCurrencies={availableCurrencies}
          />
        </Card.Header>
        <TransactionsTable
          transactions={transactions?.data}
          handlePageChange={handlePageChange}
          pagination={{ page, limit, countAll: transactions?.countAll }}
        />
      </Card>
    </>
  );
};

export default Transactions;
